// ===== Packages =====

import React, {
    useRef,
    useState,
    useEffect,
}                               from 'react';
import styled                   from 'styled-components';

// ===== Components =====

import {
    Button,
    Annotation,
}                               from '.';
import { Transition }           from '../../Transition';

// ===== Services =====

import {
    recordUserAction,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    subscribeToAnnotationEngagementItems,
    isTestMode,
} from '../../../services';

// ===== Interfaces =====

import {
    IPartialUserItem,
    IAnnotationItem,
    IAnnotationEngagementItem,
    IReadPostItem,
    ISnackbarItem,
    IDimension,
    IZoomLevelItem,
}                               from '../../../interfaces';

// ===== Enums =====

import {
    BUTTON_TYPE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    USER_ACTION_TYPE,
}                               from '../../../enums';

// ===== Constants =====

import CURSOR_SIGN              from '../../../constants/cursorSigns';
import {
    BODY_FONT_SIZE,
    ANNOTATION_BUCKET_Z_INDEX,
    DETAIL_VIEW_HANDLEBAR_CONTAINER_WIDTH,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    HOVER_TARGET_CLASSNAME,
    ANNOTATION_FOCUSED_CLOSE_BUTTON_HEIGHT,
    DETAIL_VIEW_TRANSITION_DURATION,
}                               from '../../../constants/generalConstants';

// ===== Styles =====

import { theme as themeObj }    from '../../../themes/theme-context';

interface Props {
    customCursorActive: boolean,
    user: IPartialUserItem | null,
    userEmailVerified: boolean,
    post: IReadPostItem | undefined,
    hasSound: boolean,
    currentSessionId: string | null,
    annotations: IAnnotationItem[],
    top: number,
    width: number,
    postBannerHeight: number,
    nextBucketTop: number | null,
    interactionDisabled: boolean,
    viewportDimensions: IDimension,
    postAuthorIds: string[],
    postUserItems: Map<string, IPartialUserItem>,
    postUserAvatarURLs: Map<string, string>,
    annotationAudioURLs: Map<string, string>,
    expandAnnotationStackWithAnnotationId: string | null,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    setTargetAnnotationID: React.Dispatch<React.SetStateAction<string | null>>,
    setCursorSigns: React.Dispatch<React.SetStateAction<string[]>>,
    showAnnotationDetail: (annotation: IAnnotationItem) => void,
    hideAnnotationDetail: (onlyClearAnnotationDetailData?: boolean) => void,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    clearTimeoutAnnotationNudge: () => void,
    showWarning: boolean,
    selectedPostValuePath: number[],
    currentZoomLevel: IZoomLevelItem | null,
    setRefreshNotifications: React.Dispatch<React.SetStateAction<boolean>>,
    setIsUserProfileDialogExpanded: (isExpanded: boolean) => void,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt: React.Dispatch<React.SetStateAction<boolean>>,
    removeAnnotationLocally: (annotationId: string) => void,
    refreshAnnotation: ({ annotationId }: { annotationId: string }) => void,
    enableRouteFocusSync: boolean,
    addToPostUserItems: ({ userItems }: { userItems: IPartialUserItem[] }) => Promise<void>,
    addToAnnotationAudioURLs: ({
        annotationId,
        url,
    }: {
        annotationId: string,
        url: string,
    }) => Promise<void>,
    removeFromAnnotationAudioURLs: ({ annotationId }: {annotationId: string }) => Promise<void>,
    setExpandAnnotationStackWithAnnotationId: React.Dispatch<React.SetStateAction<string | null>>,
}
function AnnotationBucket({
    customCursorActive,
    user,
    userEmailVerified,
    post,
    hasSound,
    currentSessionId,
    annotations,
    top,
    width,
    postBannerHeight,
    nextBucketTop,
    interactionDisabled,
    viewportDimensions,
    postAuthorIds,
    postUserItems,
    postUserAvatarURLs,
    annotationAudioURLs,
    expandAnnotationStackWithAnnotationId,
    onCursorEnter,
    onCursorLeave,
    setInputFocused,
    setTargetAnnotationID,
    setCursorSigns,
    showAnnotationDetail,
    hideAnnotationDetail,
    setSnackbarData,
    clearTimeoutAnnotationNudge,
    showWarning,
    selectedPostValuePath,
    currentZoomLevel,
    setRefreshNotifications,
    setIsUserProfileDialogExpanded,
    setNotifyUserToSignUpAfterAnnotationCreationAttempt,
    removeAnnotationLocally,
    refreshAnnotation,
    enableRouteFocusSync,
    addToPostUserItems,
    addToAnnotationAudioURLs,
    removeFromAnnotationAudioURLs,
    setExpandAnnotationStackWithAnnotationId,
}: Props): JSX.Element {
    // ===== General Constants =====

    const ANNOTATION_MARGIN_BOTTOM = 0.3125 * BODY_FONT_SIZE;
    const ANNOTATION_HORIZONTAL_MARGIN = 0.625 * BODY_FONT_SIZE;
    const ANNOTATION_STACK_BUTTON_HEIGHT = 1.5625 * BODY_FONT_SIZE;
    const PAGE_MARGIN_BOTTOM = 0.625 * BODY_FONT_SIZE;

    // ===== Refs =====

    const containerRef = useRef<HTMLDivElement | null>(null);

    // ===== State =====

    // stores height of annotation bucket
    const [bucketHeight, setBucketHeight] = useState<number | null>(null);
    // indicates whether bucker is in stack mode
    const [annotationStackActive, setAnnotationStackActive] = useState<boolean>(false);
    // indicates whether to expand out annotations if bucket is in stack mode
    const [annotationStackExpanded, setAnnotationStackExpanded] = useState<boolean>(false);
    // indicates whether a child annotation is focused
    const [childAnnotationIsFocused, setChildAnnotationIsFocused] = useState<string | null>(null);
    // indicates whether we should remeasure bucket height
    const [remeasureBucketHeight, setRemeasureBucketHeight] = useState<boolean>(true);
    const [annotationEngagementItems, setAnnotationEngagementItems] = useState<Map<string, IAnnotationEngagementItem>>(new Map());

    // ===== Annotation Constants =====

    const ANNOTATION_BUCKET_TRANSITION_DURATION = DETAIL_VIEW_TRANSITION_DURATION; // To sync warning transition
    const ANNOTATION_STACK_CLOSE_BUTTON_TRANSITION_DURATION = 200;

    // ===== Methods =====

    const expandAnnotationStack = async (): Promise<void> => {
        setAnnotationStackExpanded(true);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.toggleAnnotationStack,
                sessionId: currentSessionId,
                payload: {
                    expanded: true,
                },
            });
        }
    };

    const closeAnnotationStack = async (): Promise<void> => {
        onCursorLeave();
        setAnnotationStackExpanded(false);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.toggleAnnotationStack,
                sessionId: currentSessionId,
                payload: {
                    expanded: false,
                },
            });
        }
    };

    const onButtonMouseEnter = (e: React.MouseEvent): void => {
        if (onCursorEnter) {
            onCursorEnter(
                CURSOR_TARGET.editorElementButton,
                [CURSOR_SIGN.click],
                e.currentTarget as HTMLElement,
            );
        }
    };

    const onButtonMouseLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    const onShowAnnotationDetail = (annotation: IAnnotationItem): void => {
        setChildAnnotationIsFocused(annotation.id);
        showAnnotationDetail(annotation);
    };

    const onHideAnnotationDetail = (onlyClearAnnotationDetailData?: boolean): void => {
        setChildAnnotationIsFocused(null);
        hideAnnotationDetail(onlyClearAnnotationDetailData);
        // We remeasure height in case user loaded page with a focused
        // annotation which thwarted measurement process
        setRemeasureBucketHeight(true);
    };

    // ===== Side Effects =====

    // remeasure bucker height so annotation stacks
    // adjust
    useEffect(() => {
        if (remeasureBucketHeight && containerRef.current) {
            const { height } = containerRef.current.getBoundingClientRect();
            if (height !== bucketHeight) setBucketHeight(height);
            setRemeasureBucketHeight(false);
        }
    }, [
        remeasureBucketHeight,
    ]);

    useEffect(() => (
        subscribeToAnnotationEngagementItems({
            test: isTestMode(),
            // Bucket metrics should follow the canonical annotation id once an
            // optimistic row has been acked, otherwise comment/view counts lag
            // behind the visible row until the optimistic id disappears.
            annotationIds: annotations.map((annotation) => (
                (annotation as IAnnotationItem & { canonicalId?: string }).canonicalId || annotation.id
            )),
            onNext: setAnnotationEngagementItems,
        })
    ), [annotations]);

    // Determine whether to reveal all annotations or render as a stack
    useEffect(() => {
        const canStack = annotations.length > 1;
        const hasValidLayout = nextBucketTop != null && bucketHeight != null;
        const overlapsNextBucket = hasValidLayout
            ? top + bucketHeight > nextBucketTop
            : false;

        if (!canStack) {
            if (annotationStackActive) setAnnotationStackActive(false);
            if (annotationStackExpanded) setAnnotationStackExpanded(false);
            return;
        }

        if (
            hasValidLayout
            && !childAnnotationIsFocused
        ) {
            if (overlapsNextBucket && !annotationStackActive) {
                setAnnotationStackActive(true);
                const annotationIdsSet = new Set(annotations.map((a) => a.id));
                if (
                    expandAnnotationStackWithAnnotationId != null
                    && annotationIdsSet.has(expandAnnotationStackWithAnnotationId)
                ) {
                    // User created an annotation. We want them to see that it was created, but because it's
                    // part of a stack, they wouldn't see it, so we expand the stack so they see it
                    setAnnotationStackExpanded(true);
                    setExpandAnnotationStackWithAnnotationId(null);
                }
            } else if (!overlapsNextBucket && annotationStackActive) {
                setAnnotationStackActive(false);
                if (annotationStackExpanded) setAnnotationStackExpanded(false);
            }
        }
        // If a user loads the page with an annotation focused in a bucket
        // that's meant to be stacked,
        // we set expanded true because it shouldn't be possible to click
        // into an annotation without expanding.
        // This ensures that when we unfocus the annotation, the stack is expanded
        if (
            hasValidLayout
            && childAnnotationIsFocused
            && overlapsNextBucket
        ) {
            setAnnotationStackExpanded(true);
        }
    }, [
        annotations,
        top,
        bucketHeight,
        nextBucketTop,
        childAnnotationIsFocused,
        annotationStackActive,
        annotationStackExpanded,
    ]);

    return (
        <Container
            ref={containerRef}
            top={top}
            width={width}
            childIsFocused={!!childAnnotationIsFocused}
            postBannerHeight={postBannerHeight}
            pageMarginBottom={PAGE_MARGIN_BOTTOM}
            showWarning={showWarning}
            transitionDuration={ANNOTATION_BUCKET_TRANSITION_DURATION}
        >
            <Transition
                in={annotationStackExpanded && !childAnnotationIsFocused}
                timeout={{
                    enter: ANNOTATION_STACK_CLOSE_BUTTON_TRANSITION_DURATION,
                    exit: ANNOTATION_STACK_CLOSE_BUTTON_TRANSITION_DURATION,
                }}
                appear
                mountOnEnter
            >
                {
                    (state) => (
                        <AnnotationStackCloseButtonContainer
                            hide={!annotationStackExpanded || !!childAnnotationIsFocused}
                            buttonHeight={ANNOTATION_STACK_BUTTON_HEIGHT}
                            marginRight={ANNOTATION_HORIZONTAL_MARGIN}
                            marginTop={ANNOTATION_MARGIN_BOTTOM}
                            style={{
                                ...FADE_IN_DEFAULT_STYLE({
                                    direction: 'up',
                                    offset: 10,
                                    duration: ANNOTATION_STACK_CLOSE_BUTTON_TRANSITION_DURATION,
                                    easing: themeObj.motion.eagerEasing,
                                }),
                                ...FADE_IN_TRANSITION_STYLES({
                                    direction: 'up',
                                    offset: 10,
                                })[state],
                            }}
                        >
                            <Button
                                boxShadow
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                type={BUTTON_TYPE.solid}
                                background={themeObj.color.neutral800}
                                height={ANNOTATION_STACK_BUTTON_HEIGHT}
                                text="Close"
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: onButtonMouseEnter,
                                    onMouseLeave: onButtonMouseLeave,
                                })}
                                {...getPressEventHandlers({ onMouseDown: closeAnnotationStack })}
                            />
                        </AnnotationStackCloseButtonContainer>
                    )
                }
            </Transition>
            {[...annotations]
                .sort((a, b) => {
                    // Only treat rows without a canonical id as truly pending.
                    // Once acked, keep sorting them like settled annotations so
                    // they do not jump buckets solely because their UI id still
                    // has the optimistic prefix during the handoff window.
                    const aIsPending = (
                        a.id.startsWith('pending-annotation-')
                        && !(a as IAnnotationItem & { canonicalId?: string }).canonicalId
                    );
                    const bIsPending = (
                        b.id.startsWith('pending-annotation-')
                        && !(b as IAnnotationItem & { canonicalId?: string }).canonicalId
                    );
                    if (aIsPending && !bIsPending) return -1;
                    if (!aIsPending && bIsPending) return 1;

                    const aAnnotationId = (
                        a as IAnnotationItem & { canonicalId?: string }
                    ).canonicalId || a.id;
                    const bAnnotationId = (
                        b as IAnnotationItem & { canonicalId?: string }
                    ).canonicalId || b.id;
                    const aCommentCount = annotationEngagementItems.get(aAnnotationId)?.commentCount || 0;
                    const bCommentCount = annotationEngagementItems.get(bAnnotationId)?.commentCount || 0;
                    if (aCommentCount > bCommentCount) return -1;
                    if (aCommentCount < bCommentCount) return 1;

                    const aViewCount = annotationEngagementItems.get(aAnnotationId)?.viewCount || 0;
                    const bViewCount = annotationEngagementItems.get(bAnnotationId)?.viewCount || 0;
                    // Treat zero-view annotations as "unproven" rather than infinitely worse:
                    // a newer annotation with 0 views should only lose to an older one once the
                    // older one has reached a meaningful baseline of attention.
                    if (aViewCount === 0 || bViewCount === 0) {
                        if (aViewCount >= 10 && bViewCount === 0) return -1;
                        if (bViewCount >= 10 && aViewCount === 0) return 1;
                    } else {
                        if (aViewCount > 10 * bViewCount) return -1;
                        if (10 * aViewCount < bViewCount) return 1;
                    }

                    if (a.published < b.published) return 1;
                    if (a.published > b.published) return -1;
                    return 0;
                })
                .map((annotation, index) => (
                    <Annotation
                        key={annotation.id}
                        customCursorActive={customCursorActive}
                        annotation={annotation}
                        enableRouteFocusSync={enableRouteFocusSync}
                        hasSound={hasSound}
                        user={user}
                        userEmailVerified={userEmailVerified}
                        post={post}
                        bucketWidth={width}
                        currentSessionId={currentSessionId}
                        annotationStackActive={annotationStackActive}
                        index={index}
                        horizontalMargin={ANNOTATION_HORIZONTAL_MARGIN}
                        zIndex={annotations.length - index}
                        annotationCount={annotations.length}
                        hide={!!childAnnotationIsFocused && childAnnotationIsFocused !== annotation.id}
                        interactionDisabled={interactionDisabled}
                        annotationStackExpanded={annotationStackExpanded}
                        bucketHeight={bucketHeight}
                        viewportDimensions={viewportDimensions}
                        postAuthorIds={postAuthorIds}
                        postUserItems={postUserItems}
                        postUserAvatarURLs={postUserAvatarURLs}
                        annotationAudioURLs={annotationAudioURLs}
                        remeasureBucketHeight={remeasureBucketHeight}
                        onCursorEnter={onCursorEnter}
                        onCursorLeave={onCursorLeave}
                        setInputFocused={setInputFocused}
                        setTargetAnnotationID={setTargetAnnotationID}
                        setCursorSigns={setCursorSigns}
                        showAnnotationDetail={onShowAnnotationDetail}
                        hideAnnotationDetail={onHideAnnotationDetail}
                        setSnackbarData={setSnackbarData}
                        setRemeasureBucketHeight={setRemeasureBucketHeight}
                        clearTimeoutAnnotationNudge={clearTimeoutAnnotationNudge}
                        {...(index === 0 && annotations.length > 0 && annotationStackActive
                            ? { toggleAnnotationStack: expandAnnotationStack }
                            : {}
                        )}
                        selectedPostValuePath={selectedPostValuePath}
                        currentZoomLevel={currentZoomLevel}
                        setRefreshNotifications={setRefreshNotifications}
                        setIsUserProfileDialogExpanded={setIsUserProfileDialogExpanded}
                        setNotifyUserToSignUpAfterAnnotationCreationAttempt={setNotifyUserToSignUpAfterAnnotationCreationAttempt}
                        removeAnnotationLocally={removeAnnotationLocally}
                        refreshAnnotation={refreshAnnotation}
                        addToPostUserItems={addToPostUserItems}
                        addToAnnotationAudioURLs={addToAnnotationAudioURLs}
                        removeFromAnnotationAudioURLs={removeFromAnnotationAudioURLs}
                    />
                ))}
        </Container>
    );
}

// ===== Styled Components =====

interface ContainerProps {
    top: number,
    width: number,
    childIsFocused: boolean,
    postBannerHeight: number,
    pageMarginBottom: number,
    showWarning: boolean,
    transitionDuration: number,
}
const Container = styled.div<ContainerProps>`
    position: ${({ childIsFocused }) => (childIsFocused ? 'fixed' : 'absolute')};
    top: ${({
        childIsFocused,
        postBannerHeight,
        top,
    }) => {
        if (!childIsFocused) return `${top}px`;
        const focusedVerticalOffset = ANNOTATION_FOCUSED_CLOSE_BUTTON_HEIGHT + 10;
        return `calc(var(--reader-post-banner-height, ${postBannerHeight}px) + ${focusedVerticalOffset}px)`;
    }};
    left: ${({ childIsFocused }) => (childIsFocused
        ? 'auto'
        : `${DETAIL_VIEW_HANDLEBAR_CONTAINER_WIDTH}px`
    )};
    right: ${({ childIsFocused }) => (childIsFocused
        ? '0px'
        : 'auto'
    )};
    width: ${({ width }) => `${width}px`};
    height: ${({
        childIsFocused,
        postBannerHeight,
        pageMarginBottom,
    }) => {
        if (!childIsFocused) return 'auto';
        const focusedVerticalOffset = ANNOTATION_FOCUSED_CLOSE_BUTTON_HEIGHT + 10 + pageMarginBottom;
        return `calc(100vh - (var(--reader-post-banner-height, ${postBannerHeight}px) + ${focusedVerticalOffset}px))`;
    }};
    height: ${({
        childIsFocused,
        postBannerHeight,
        pageMarginBottom,
    }) => {
        if (!childIsFocused) return 'auto';
        const focusedVerticalOffset = ANNOTATION_FOCUSED_CLOSE_BUTTON_HEIGHT + 10 + pageMarginBottom;
        return `calc(100svh - (var(--reader-post-banner-height, ${postBannerHeight}px) + ${focusedVerticalOffset}px))`;
    }};
    height: ${({
        childIsFocused,
        postBannerHeight,
        pageMarginBottom,
    }) => {
        if (!childIsFocused) return 'auto';
        const focusedVerticalOffset = ANNOTATION_FOCUSED_CLOSE_BUTTON_HEIGHT + 10 + pageMarginBottom;
        return `calc(100dvh - (var(--reader-post-banner-height, ${postBannerHeight}px) + ${focusedVerticalOffset}px))`;
    }};
    background: ${({ showWarning }) => {
        if (showWarning) {
            return 'inherit';
        }

        return 'transparent';
    }};
    z-index: ${({ childIsFocused }) => (childIsFocused
        ? ANNOTATION_BUCKET_Z_INDEX
        : 'auto')};
    transition: ${({ transitionDuration, theme }) => `
        background ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    ${({ theme }) => theme.mediaQuery.small} {
        left: 0px;
        background: ${({ theme }) => theme.color.cream500};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        left: 0px;
        background: ${({ theme }) => theme.color.cream500};
    }
`;

interface AnnotationStackCloseButtonContainerProps {
    hide: boolean,
    buttonHeight: number,
    marginRight: number,
    marginTop: number,
}
const AnnotationStackCloseButtonContainer = styled.div<AnnotationStackCloseButtonContainerProps>`
    position: absolute;
    top: ${({ buttonHeight, marginTop }) => `-${buttonHeight + marginTop}px`};
    right: 0px;
    margin-right: ${({ marginRight }) => `${marginRight}px`};
    margin-bottom: ${`${0.625 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    pointer-events: ${({ hide }) => (hide ? 'none' : 'auto')};
    visibility: ${({ hide }) => (hide ? 'hidden' : 'visible')};
`;

export default AnnotationBucket;
